<template>
  <div class="v2026-tr">

    <div class="v2026-tr__head">
      <span class="v2026-tr__title">ผู้ช่วย AI แปลภาษา</span>
      <span class="v2026-tr-mask v2026-tr__head-ico" aria-hidden="true"
            :style="maskStyle(icons.sparkles)"></span>
    </div>

    <div class="v2026-tr__field">
      <label class="v2026-tr__label" :for="languageId">เลือกภาษาที่ต้องการแปล</label>
      <select :id="languageId" class="v2026-tr__select" v-model="language">
        <option value="th">ภาษาไทย</option>
        <option value="en">ภาษาอังกฤษ</option>
        <option value="my-MM">ภาษาพม่า</option>
        <option value="lo-LA">ภาษาลาว</option>
        <option value="km-KH">ภาษากัมพูชา</option>
      </select>
    </div>

    <div class="v2026-tr__field">
      <textarea ref="input"
                class="v2026-tr__textarea"
                rows="4"
                aria-label="ข้อความที่ต้องการแปล"
                placeholder="มีอะไรให้ฉันแปลบ้างไหม?"
                v-model="input"
                @input="fieldHint = null"
                @keydown.shift.enter.prevent="translate"></textarea>
      <div class="v2026-tr__hint" role="alert" v-if="fieldHint">* {{ fieldHint }}</div>
      <div class="v2026-tr__count" v-if="input">{{ input.length }} ตัวอักษร</div>
    </div>

    <div class="v2026-tr__actions">
      <button type="button" class="v2026-tr__ghost" v-if="canClear" @click="clear">ล้าง</button>
      <button type="button" class="v2026-tr__primary" :disabled="!canTranslate" @click="translate">
        <span class="v2026-tr-mask v2026-tr__primary-ico" aria-hidden="true"
              :style="maskStyle(icons.sparkles)"></span>
        <span class="v2026-tr__primary-text">{{ isTranslating ? 'กำลังแปล…' : 'แปลข้อความ' }}</span>
      </button>
    </div>

    <span class="v2026-tr__section-label">ผลการแปล</span>
    <div class="v2026-tr__result" role="status" aria-live="polite">
      <div class="v2026-tr__loading" v-if="isTranslating">
        <span class="v2026-tr__spinner" aria-hidden="true"></span>
        <span>กำลังแปลข้อความ<span class="v2026-tr__dots"><i></i><i></i><i></i></span></span>
      </div>
      <template v-else-if="errorText">
        <div class="v2026-tr__bullet v2026-tr__error" role="alert">{{ errorText }}</div>
        <div class="v2026-tr__result-actions">
          <button type="button" class="v2026-tr__outline" @click="translate">
            <span class="v2026-tr-mask v2026-tr__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.retry)"></span>
            <span>ลองอีกครั้ง</span>
          </button>
        </div>
      </template>
      <template v-else-if="translation">
        <div class="v2026-tr__text">{{ translation }}</div>
        <div class="v2026-tr__result-actions">
          <button type="button" class="v2026-tr__outline" @click="copyResult">
            <span class="v2026-tr-mask v2026-tr__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.copy)"></span>
            <span>คัดลอก</span>
          </button>
          <button type="button" class="v2026-tr__outline v2026-tr__outline--main" @click="sendToComposer">
            <span class="v2026-tr-mask v2026-tr__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.send)"></span>
            <span>ส่งไปช่องพิมพ์</span>
          </button>
        </div>
      </template>
      <div class="v2026-tr__bullet" v-else>• พิมพ์ข้อความแล้วกด "แปลข้อความ"</div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'translate-assistant-v2026',
  props: {
    // Only drives the reset watcher; the endpoint is conversation-agnostic.
    selectedConversation: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      language: 'th',
      input: '',
      translation: null,
      errorText: null,
      fieldHint: null,
      isTranslating: false,
      requestToken: 0,
      insertToken: 0,
      icons: {
        sparkles: require('@/assets/icons2026/ai-magic.svg'),
        copy: require('@/assets/icons2026/document.svg'),
        send: require('@/assets/icons2026/composer-send.svg'),
        retry: require('@/assets/icons2026/refresh.svg')
      }
    }
  },
  computed: {
    languageId () {
      return `v2026-tr-lang-${this._uid}`
    },
    canTranslate () {
      return this.input.trim().length > 0 && !this.isTranslating
    },
    canClear () {
      return !!(this.input || this.translation || this.errorText)
    }
  },
  watch: {
    'selectedConversation.id' () {
      this.input = ''
      this.translation = null
      this.errorText = null
      this.fieldHint = null
      this.isTranslating = false
      this.requestToken++
    }
  },
  methods: {
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    clear () {
      this.input = ''
      this.translation = null
      this.errorText = null
      this.fieldHint = null
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus()
      })
    },
    async translate () {
      this.fieldHint = null
      if (!this.input.trim()) {
        this.fieldHint = 'กรุณากรอกข้อความที่ต้องการแปล'
        return
      }
      const token = ++this.requestToken
      this.isTranslating = true
      this.translation = null
      this.errorText = null
      try {
        const { data } = await axios.post('/ai-assistant/translate-lang', {
          inputText: this.input,
          targetLang: this.language
        })
        if (token !== this.requestToken) return
        const text = data && data.result && data.result.translation
        if (typeof text === 'string' && text.trim()) {
          this.translation = text
        } else {
          this.errorText = 'ไม่ได้รับผลการแปลจากระบบ ลองใหม่อีกครั้ง'
        }
      } catch (e) {
        if (token !== this.requestToken) return
        this.errorText = 'เกิดข้อผิดพลาด ลองใหม่'
      } finally {
        // A stale response must not clear the spinner of the request that superseded it.
        if (token === this.requestToken) this.isTranslating = false
      }
    },
    async copyResult () {
      try {
        await navigator.clipboard.writeText(this.translation)
        this.$emit('notify', { variant: 'success', title: 'คัดลอกข้อความแล้ว', message: '' })
      } catch (e) {
        this.$emit('notify', { variant: 'danger', title: 'คัดลอกไม่สำเร็จ', message: 'กรุณาคัดลอกด้วยตนเอง' })
      }
    },
    sendToComposer () {
      if (!this.translation) return
      const payload = { text: this.translation, token: ++this.insertToken }
      this.$store.commit('aiAssistant/SET_COMPOSER_INSERT', payload)
      // The composer resolves append-vs-replace and stamps the payload; its watcher runs
      // before this callback, so the toast can report what actually happened.
      this.$nextTick(() => {
        this.$emit('notify', {
          variant: 'success',
          title: payload.appended ? 'เพิ่มคำแปลต่อท้ายข้อความเดิมแล้ว' : 'ใส่คำแปลในช่องพิมพ์แล้ว',
          message: ''
        })
      })
    }
  }
}
</script>

<style scoped>
.v2026-tr-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}

.v2026-tr {
  display: flex;
  flex-direction: column;
  padding: var(--v2026-sp-4) var(--v2026-sp-4) 0;
  gap: var(--v2026-sp-3);
}

.v2026-tr__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-3);
}
.v2026-tr__title {
  font-size: var(--v2026-fs-name);
  font-weight: 700;
  color: var(--v2026-text);
}
.v2026-tr__head-ico {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  background-color: var(--v2026-color-main);
}

.v2026-tr__field {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-tr__label {
  margin: 0;
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}
.v2026-tr__select {
  width: 100%;
  height: 44px;
  padding: 0 var(--v2026-sp-3);
  border: 1px solid #EBEDF0;
  border-radius: 8px;
  background: var(--v2026-surface);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
}
.v2026-tr__select:focus {
  outline: none;
  border-color: var(--v2026-color-main);
}
.v2026-tr__textarea {
  width: 100%;
  resize: vertical;
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
  border: 1px solid #EBEDF0;
  border-radius: var(--v2026-r-card);
  background: var(--v2026-surface);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  line-height: 1.5;
}
.v2026-tr__textarea:focus {
  outline: none;
  border-color: var(--v2026-color-main);
}
.v2026-tr__hint {
  font-size: var(--v2026-fs-meta);
  color: #f04438;
}
.v2026-tr__count {
  align-self: flex-end;
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
}

.v2026-tr__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--v2026-sp-2);
}
.v2026-tr__ghost {
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
}
.v2026-tr__ghost:hover {
  background: var(--v2026-surface-hover);
}
.v2026-tr__ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-tr__primary {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: linear-gradient(135deg, #2f6bff 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
}
.v2026-tr__primary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.30);
}
.v2026-tr__primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.v2026-tr__primary-ico {
  width: 16px;
  height: 16px;
  background-color: #ffffff;
}
.v2026-tr__primary-text {
  white-space: nowrap;
}

.v2026-tr__section-label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}
.v2026-tr__result {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}
.v2026-tr__bullet {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-tr__error {
  color: #f04438;
}
.v2026-tr__text {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}
.v2026-tr__result-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-2);
}
.v2026-tr__outline {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
}
.v2026-tr__outline:hover {
  background: var(--v2026-surface-hover);
}
.v2026-tr__outline:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-tr__outline-ico {
  width: 16px;
  height: 16px;
  background-color: currentColor;
}
.v2026-tr__outline--main {
  border-color: var(--v2026-color-main);
  color: var(--v2026-color-main);
}
.v2026-tr__outline--main:hover {
  background: var(--v2026-color-main-soft);
}

.v2026-tr__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--v2026-text-muted);
}
.v2026-tr__spinner {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  border: 2px solid rgba(124, 58, 237, 0.25);
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: v2026-tr-spin 0.7s linear infinite;
}
@keyframes v2026-tr-spin {
  to { transform: rotate(360deg); }
}
.v2026-tr__dots i {
  display: inline-block;
  width: 3px;
  height: 3px;
  margin-left: 2px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.3;
  animation: v2026-tr-dot 1.2s infinite;
}
.v2026-tr__dots i:nth-child(2) { animation-delay: 0.2s; }
.v2026-tr__dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes v2026-tr-dot {
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
}
</style>
